import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders, HttpInterceptorFn, HttpRequest, } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, Observable, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { AuthTokens } from '../../features/staff/auth/models/staff-auth.models';
import { TokenService } from '../services/token/token.service';
import { SKIP_AUTH } from './auth-http-context';
const REFRESH_TOKEN_PATH = '/auth/refresh-tokens';
let refreshRequest$: Observable<AuthTokens> | null = null;
export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const tokenService = inject(TokenService);
  const httpBackend = inject(HttpBackend);
  const router = inject(Router);
  if (request.context.get(SKIP_AUTH) || !isApiRequest(request.url))
    return next(request);
  const accessToken = tokenService.getAccessToken();
  const authorizedRequest = accessToken ? addBearerToken(request, accessToken) : request;
  return next(authorizedRequest).pipe(catchError((error: unknown) => {
    if (!shouldRefresh(error, request, tokenService))
      return throwError(() => error);
    return refreshTokens(tokenService, httpBackend).pipe(switchMap(tokens => next(addBearerToken(request, tokens.access_token))), catchError((refreshError: unknown) => {
      tokenService.clearTokens();
      void router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
      return throwError(() => refreshError);
    }));
  }));
};
function refreshTokens(tokenService: TokenService, httpBackend: HttpBackend): Observable<AuthTokens> {
  if (refreshRequest$)
    return refreshRequest$;
  const refreshToken = tokenService.getRefreshToken();
  if (!refreshToken)
    return throwError(() => new Error('Refresh token отсутствует.'));
  const http = new HttpClient(httpBackend);
  const headers = new HttpHeaders({ Authorization: `Bearer ${refreshToken}` });
  refreshRequest$ = http.post<AuthTokens>(createApiUrl(REFRESH_TOKEN_PATH), null, { headers }).pipe(tap(tokens => tokenService.setTokens(tokens)), finalize(() => { refreshRequest$ = null; }), shareReplay({ bufferSize: 1, refCount: false }));
  return refreshRequest$;
}
function shouldRefresh(error: unknown, request: HttpRequest<unknown>, tokenService: TokenService): boolean {
  return error instanceof HttpErrorResponse
    && error.status === 401
    && !request.url.includes(REFRESH_TOKEN_PATH)
    && Boolean(tokenService.getRefreshToken());
}
function addBearerToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
function createApiUrl(path: string): string {
  return `${environment.apiUrl.replace(/\/+$/, '')}${path}`;
}
function isApiRequest(url: string): boolean {
  const apiUrl = environment.apiUrl.replace(/\/+$/, '');
  return apiUrl ? url.startsWith(apiUrl) : !/^https?:\/\//i.test(url);
}
